import React, { useContext, useEffect } from 'react';

import { AmpPropsContext } from '../../context/amp-props/amp-props-provider';
import { useSkyPlayerContext } from '../../context/sky-player/sky-player-context';
import t from '../../labels/labelling-utils';
import { IAmpPropsTag } from '../../typings/amp';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { getAmpTag } from '../../utils/amp';
import { IClassNames, makeClassNames } from '../../utils/styles';
import AmpVideo from '../amp/amp-video';
import './post-video.scss';

// setting to have the videos in audio on in liveblog
const DEFAULT_MUTE_VALUE = false;

export interface IPostVideo {
  containerId?: string;
  videoId: string | number;
  domain?: string;
  live?: boolean;
  autostart?: boolean;
  advOff?: boolean;
  daiOff?: boolean;
  social?: boolean;
  videoDescription?: string;
  videoPageUrl?: string;
  tenant?: LiveblogTenant;
  isWebView?: boolean;
}

const DEFAULT_DOMAIN = 'sky';
const DEFAULT_WEBVIEW_VIDEO_SOCIAL = false;

const classNames = (): IClassNames =>
  makeClassNames({
    PostVideo: 'post-video-v2',
    PostVideoPositioner: 'post-video__positioner',
    PostVideoDescription: 'post-video__description-v2',
    PostVideoWebviewLink: 'post-video__webview-link',
    PostVideoAmpLink: 'post-video__amp-link',
  });

const createVideoId = (containerId: string, videoId: string | number) => `${containerId}-${videoId}`;

const PostVideo: React.FC<IPostVideo> = ({
  containerId = 'lvbg-video-player',
  videoId,
  videoDescription,
  videoPageUrl,
  live,
  autostart,
  domain = DEFAULT_DOMAIN,
  advOff = false,
  daiOff = false,
  social = true,
  tenant,
  isWebView,
}) => {
  const cn = classNames();
  const htmlId = createVideoId(containerId, videoId);
  const { ampProps } = useContext(AmpPropsContext);
  const ampVideo: IAmpPropsTag | undefined = getAmpTag(ampProps, 'video');

  const { skyPlayersManager } = useSkyPlayerContext();

  const isSportWebview = isWebView && tenant === 'sport';

  // Start of the override necessary to fix
  // temporarily the issue of the videos play button
  // ITE-3927 , remove when Video team does their implementation
  // TO DELETE IN FUTURE - TEMPORARY FIX ON VIDEO PLAYER
  useEffect(() => {
    const observer = new MutationObserver((mutations, observer) => {
      const lvbgPostVideoElements = document.getElementsByClassName('lvbg-post-video');
      const lvbgPostVideoArray = Array.from(lvbgPostVideoElements);
      for (const element of lvbgPostVideoArray) {
        const dataElements = element.getElementsByClassName('data');
        const dataArray = Array.from(dataElements);
        dataArray.forEach((dataElement) => {
          if (dataElement instanceof HTMLElement) {
            dataElement.style.pointerEvents = 'none';
          }
        });
      }
    });

    observer.observe(document, {
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, []);
  // TO DELETE IN FUTURE - TEMPORARY FIX ON VIDEO PLAYER

  useEffect(() => {
    if (!isSportWebview && !ampVideo) {
      skyPlayersManager?.initPlayer(htmlId, {
        id: videoId,
        domain,
        live,
        autostart,
        mute: DEFAULT_MUTE_VALUE,
        advOff,
        daiOff,
        social: isWebView ? DEFAULT_WEBVIEW_VIDEO_SOCIAL : social,
      });
    }
  }, [skyPlayersManager, videoId, ampVideo]);

  // link to video only for Sport webview
  if (isSportWebview) {
    return (
      <div className={cn.PostVideoWebviewLink}>
        <a href={videoPageUrl}>{t('post.video.go.to.video')}</a>
      </div>
    );
  }

  if (ampVideo) {
    return <AmpVideo className={cn.PostVideoAmpLink} videoPageUrl={videoPageUrl} />;
  }

  return (
    <div className={cn.PostVideo}>
      <div className={cn.PostVideoPositioner}>
        <div id={htmlId} />
      </div>
      {videoDescription && <div className={cn.PostVideoDescription}>{videoDescription}</div>}
    </div>
  );
};

export default PostVideo;
